import { ProfilePreviewInfo } from '@psl/flows/profiles/components/WiredProfileAboutContent';
import { useIsSellerApplicationFlowActive } from '@psl/flows/seller-application/functions/useIsSellerApplicationFlowActive';
import { useIsBusinessLocationValid } from '@psl/flows/seller-onboarding/check-items/business-location';
import { useIsCommentChatValid } from '@psl/flows/seller-onboarding/check-items/commentchat';
import { useInventoryListingCount } from '@psl/flows/seller-onboarding/check-items/inventory-listings';
import { useIsShippingOptionsValid } from '@psl/flows/seller-onboarding/check-items/shipping-options';
import { useStorFavoritesListingCount } from '@psl/flows/seller-onboarding/check-items/store-favorites';
import { useIsStoreInfoValid } from '@psl/flows/seller-onboarding/check-items/store-info';
import {
  kDefaultMinimumInventoryListingCount,
  kDefaultMinimumPublicListingCount,
  kStepsInfo,
} from '@psl/flows/seller-onboarding/constants';
import { getDefaultCheckListConfig } from '@psl/flows/seller-onboarding/functions/useDefaultCheckListConfig';
import {
  SellerOnboardingDefaultInitializer,
  useDefaultContextProps,
} from '@psl/flows/seller-onboarding/initializers/default';
import { SellerOnboardingPropsContext } from '@psl/flows/seller-onboarding/Provider';
import {
  SellerOnboardingContextProps,
  StepState,
  SynthesizedSellerOnboardingCheckListConfig,
} from '@psl/flows/seller-onboarding/types';
import { useStore } from '@psl/flows/showcase/functions/useCurrentShow/useCurrentShow';
import {
  useCurrentUserPrivateProfile,
  useCurrentUserProfile,
  useCurrentUserReadonlyProfile,
} from '@psl/functions/auth/utils';
import { isDebugEnabled } from '@psl/functions/debugging';
import { WithID } from '@psl/functions/firebase/rxfireExtensions';
import { useCreation } from '@psl/functions/hooks/useCreation';
import { usePublicConfig } from '@psl/functions/hooks/usePublicConfig';
import { useOpenUserProfile } from '@psl/functions/hooks/useUserProfileHandler';
import { isSellerProfile } from '@psl/functions/models/profiles';
import { usePslPay } from '@psl/functions/pslpay/usePslPay';
import { usePendingStoreReelUpload } from '@psl/functions/seller/usePendingStoreReelUpload';
import {
  PrivateProfile,
  Profile,
  PublicConfig,
  ReadonlyProfile,
  SellerOnboardingStepType,
  StoreFront,
  StoreType,
} from '@psl/models/ClientModels';
import { makeDebug } from '@psl/utils/makeDebug';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import isAfter from 'date-fns/isAfter';
import merge from 'lodash.merge';
import { ascend } from 'ramda';
import { memo, useContext, useEffect, useMemo, useRef } from 'react';
import { BehaviorSubject } from 'rxjs';

const debug = makeDebug(
  'seller-onboarding',
  isDebugEnabled('sellerOnboarding'),
);

export const SellerOnboardingStandardInitializer = memo(() => {
  const profile = useCurrentUserProfile();
  const isSeller = profile != null && isSellerProfile(profile);
  const isSellerApplicationActive = useIsSellerApplicationFlowActive();
  if (!isSeller || isSellerApplicationActive) {
    return <SellerOnboardingDefaultInitializer />;
  }
  return <SellerOnboardingInitializerContent />;
});

export default SellerOnboardingStandardInitializer;

const SellerOnboardingInitializerContent = memo(() => {
  const { props$ } = useContext(SellerOnboardingPropsContext);

  const profile = useCurrentUserProfile();
  const privateProfile = useCurrentUserPrivateProfile();
  const readonlyProfile = useCurrentUserReadonlyProfile();
  const store = useStore(profile?.id);
  const publicConfig = usePublicConfig();
  const defaultProps = useDefaultContextProps(publicConfig.seller_onboarding);
  const checkListConfig = useSynthesizedCheckListConfig({
    profile,
    privateProfile,
    readonlyProfile,
    store,
    publicConfig,
  });

  const enforcedAfter =
    checkListConfig.enforced_after ??
    publicConfig.seller_onboarding?.enforced_after;

  const limitInDays =
    checkListConfig.limit_in_days ??
    publicConfig.seller_onboarding?.limit_in_days;

  const shouldEnforce =
    enforcedAfter == null ||
    (profile?.createdAt != null &&
      isAfter(profile.createdAt.toDate(), enforcedAfter.toDate()));

  const elapsedInDays = store?.createdAt
    ? differenceInSeconds(new Date(), store.createdAt.toDate()) / (24 * 3600)
    : null;

  const timeLimitExceeded =
    elapsedInDays != null && limitInDays != null && elapsedInDays > limitInDays;

  __DEV__ &&
    debug(
      'store id: %s, createdAt: %s, limit in days: %s, enforce after: %s, ' +
        'elapsed in days: %s, time limit exceeded: %s',
      store?.id,
      store?.createdAt.toDate().toISOString(),
      limitInDays,
      enforcedAfter?.toDate().toLocaleDateString(),
      elapsedInDays,
      timeLimitExceeded,
    );

  let active = true;

  if (
    profile == null ||
    privateProfile == null ||
    readonlyProfile == null ||
    store == null ||
    !publicConfig.enable_seller_onboarding ||
    privateProfile?.sellerOnboardingCompleted ||
    !shouldEnforce ||
    timeLimitExceeded
  ) {
    active = false;
  }

  useEffect(() => {
    if (!active) {
      props$.next(defaultProps);
    }
  }, [props$, active, defaultProps]);

  const defaultPropsRef = useRef(defaultProps);

  useEffect(
    () => () => {
      // restore to default props on unmount in case user logout, etc.
      props$.next(defaultPropsRef.current);
    },
    [props$],
  );

  if (!active) {
    return null;
  }

  return (
    <Content
      profile={profile!}
      privateProfile={privateProfile!}
      readonlyProfile={readonlyProfile!}
      store={store!}
      publicConfig={publicConfig}
      checkListConfig={checkListConfig}
    />
  );
});

const Content = memo(
  ({
    profile,
    privateProfile,
    readonlyProfile,
    store,
    publicConfig,
    checkListConfig,
  }: {
    profile: WithID<Profile>;
    privateProfile: WithID<PrivateProfile>;
    readonlyProfile: WithID<ReadonlyProfile>;
    store: WithID<StoreFront>;
    publicConfig: Partial<PublicConfig>;
    checkListConfig: SynthesizedSellerOnboardingCheckListConfig;
  }) => {
    const sellerId = profile.id;

    const { props$ } = useContext(SellerOnboardingPropsContext);

    const pendingReel = usePendingStoreReelUpload(sellerId);

    const { isPslPayConnected } = usePslPay(
      profile,
      privateProfile,
      readonlyProfile,
      store,
      publicConfig,
    );

    const isCommentchatOnboardingOptional =
      readonlyProfile.features?.isCommentchatOnboardingOptional;

    const isStoreInfoValid = useIsStoreInfoValid(store, profile);
    const isStoreReelValid = store.evergreenVideo?.videoURL != null;
    const isStoreReelPending = pendingReel != null;
    const isBusinessLocationValid = useIsBusinessLocationValid(privateProfile);
    const isShippingOptionsValid = useIsShippingOptionsValid(store);
    const isCommentChatValid = useIsCommentChatValid(privateProfile);

    debug('checkListConfig: %O', checkListConfig);
    debug('isStoreReelPending: ', isStoreReelPending);

    const minInventoryListingCount =
      checkListConfig.inventory_listings?.min_listing_count ??
      kDefaultMinimumInventoryListingCount;

    const minPublicListingCount =
      checkListConfig.store_favorites?.min_listing_count ??
      kDefaultMinimumPublicListingCount;

    const inventoryListingCount = useInventoryListingCount(
      profile,
      minInventoryListingCount,
      !checkListConfig.inventory_listings?.required,
    );
    const publicListingCount = useStorFavoritesListingCount(
      profile,
      minPublicListingCount,
      !checkListConfig.store_favorites?.required,
    );

    const openProfile = useOpenUserProfile();

    const stepsStatus: {
      [key in SellerOnboardingStepType]: StepState;
    } = useMemo(() => {
      return {
        [SellerOnboardingStepType.PslPay]: {
          ...kStepsInfo[SellerOnboardingStepType.PslPay],
          name: checkListConfig.psl_pay?.title ?? 'Get Paid via Stripe',
          description: checkListConfig.psl_pay?.description,
          valid: isPslPayConnected,
          skipped: !checkListConfig.psl_pay?.required,
          sortOrder: checkListConfig.psl_pay?.sort_order ?? 0,
          params: () => ({
            isPslPayConnected,
          }),
        },
        [SellerOnboardingStepType.StoreInfo]: {
          ...kStepsInfo[SellerOnboardingStepType.StoreInfo],
          name: checkListConfig.store_info?.title ?? 'Store Info',
          description: checkListConfig.store_info?.description,
          valid: Boolean(
            isStoreInfoValid && privateProfile?.storeInfoConfirmed,
          ),
          skipped: !checkListConfig.store_info?.required,
          sortOrder: checkListConfig.store_info?.sort_order ?? 0,
          params: () => ({
            onPreview: (preview: ProfilePreviewInfo) => {
              void openProfile({ uid: sellerId, preview });
            },
          }),
        },
        [SellerOnboardingStepType.StoreReel]: {
          ...kStepsInfo[SellerOnboardingStepType.StoreReel],
          name: checkListConfig.store_reel?.title ?? 'Store Reel',
          description: checkListConfig.store_reel?.description,
          valid: Boolean(isStoreReelValid),
          skipped: !checkListConfig.store_reel?.required,
          loading: isStoreReelPending,
          subTitle: isStoreReelPending ? 'Processing... ' : undefined,
          sortOrder: checkListConfig.store_reel?.sort_order ?? 0,
        },
        [SellerOnboardingStepType.BusinessLocation]: {
          ...kStepsInfo[SellerOnboardingStepType.BusinessLocation],
          name: checkListConfig.business_location?.title ?? 'Business Location',
          description: checkListConfig.business_location?.description,
          skipped: !checkListConfig.business_location?.required,
          valid: isBusinessLocationValid,
          sortOrder: checkListConfig.business_location?.sort_order ?? 0,
        },
        [SellerOnboardingStepType.ShippingOptions]: {
          ...kStepsInfo[SellerOnboardingStepType.ShippingOptions],
          name: checkListConfig.shipping_options?.title ?? 'Shipping Options',
          description: checkListConfig.shipping_options?.description,
          skipped: !checkListConfig.shipping_options?.required,
          valid: isShippingOptionsValid,
          sortOrder: checkListConfig.shipping_options?.sort_order ?? 0,
        },
        [SellerOnboardingStepType.Inventory]: {
          ...kStepsInfo[SellerOnboardingStepType.Inventory],
          name: checkListConfig.inventory_listings?.title ?? 'Inventory',
          description: checkListConfig.inventory_listings?.description?.replace(
            '{limit}',
            `${minInventoryListingCount}`,
          ),
          skipped: !checkListConfig.inventory_listings?.required,
          valid: inventoryListingCount >= minInventoryListingCount,
          sortOrder: checkListConfig.inventory_listings?.sort_order ?? 0,
          subTitle:
            inventoryListingCount < minInventoryListingCount
              ? `${inventoryListingCount} out of ${minInventoryListingCount}`
              : undefined,
        },
        [SellerOnboardingStepType.Storefront]: {
          ...kStepsInfo[SellerOnboardingStepType.Storefront],
          name: checkListConfig.store_favorites?.title ?? 'Storefront',
          skipped: !checkListConfig.store_favorites?.required,
          valid: publicListingCount >= minPublicListingCount,
          description: checkListConfig.store_favorites?.description?.replace(
            '{limit}',
            `${minPublicListingCount}`,
          ),
          subTitle:
            publicListingCount < minPublicListingCount
              ? `${publicListingCount} out of ${minPublicListingCount}`
              : undefined,
          sortOrder: checkListConfig.store_favorites?.sort_order ?? 0,
          params: () => ({
            isSellerOnboardingMode: true,
          }),
        },
        [SellerOnboardingStepType.CommentChat]: {
          ...kStepsInfo[SellerOnboardingStepType.CommentChat],
          name: checkListConfig.commentchat?.title ?? 'CommentChat',
          description: checkListConfig.commentchat?.description,
          valid: Boolean(isCommentChatValid),
          skipped:
            !checkListConfig.commentchat?.required ||
            isCommentchatOnboardingOptional,
          sortOrder: checkListConfig.commentchat?.sort_order ?? 0,
        },
      };
    }, [
      checkListConfig.business_location?.description,
      checkListConfig.business_location?.required,
      checkListConfig.business_location?.sort_order,
      checkListConfig.business_location?.title,
      checkListConfig.commentchat?.description,
      checkListConfig.commentchat?.required,
      checkListConfig.commentchat?.sort_order,
      checkListConfig.commentchat?.title,
      checkListConfig.inventory_listings?.description,
      checkListConfig.inventory_listings?.required,
      checkListConfig.inventory_listings?.sort_order,
      checkListConfig.inventory_listings?.title,
      checkListConfig.psl_pay?.description,
      checkListConfig.psl_pay?.required,
      checkListConfig.psl_pay?.sort_order,
      checkListConfig.psl_pay?.title,
      checkListConfig.shipping_options?.description,
      checkListConfig.shipping_options?.required,
      checkListConfig.shipping_options?.sort_order,
      checkListConfig.shipping_options?.title,
      checkListConfig.store_favorites?.description,
      checkListConfig.store_favorites?.required,
      checkListConfig.store_favorites?.sort_order,
      checkListConfig.store_favorites?.title,
      checkListConfig.store_info?.description,
      checkListConfig.store_info?.required,
      checkListConfig.store_info?.sort_order,
      checkListConfig.store_info?.title,
      checkListConfig.store_reel?.description,
      checkListConfig.store_reel?.required,
      checkListConfig.store_reel?.sort_order,
      checkListConfig.store_reel?.title,
      inventoryListingCount,
      isBusinessLocationValid,
      isCommentChatValid,
      isCommentchatOnboardingOptional,
      isPslPayConnected,
      isShippingOptionsValid,
      isStoreInfoValid,
      isStoreReelPending,
      isStoreReelValid,
      minInventoryListingCount,
      minPublicListingCount,
      openProfile,
      privateProfile?.storeInfoConfirmed,
      publicListingCount,
      sellerId,
    ]);

    const steps$ = useCreation(() => new BehaviorSubject<StepState[]>([]), []);

    useEffect(() => {
      steps$.next(
        [...Object.values(stepsStatus)]
          .filter(x => !x.skipped)
          .sort((x, y) => ascend(t => t.sortOrder ?? 0, x, y)),
      );
    }, [stepsStatus, steps$]);

    const forceCompletionBeforeProceeding =
      checkListConfig.force_completion_before_proceeding_in_app ?? false;

    const props = useMemo<SellerOnboardingContextProps>(
      () => ({
        enabled: true,
        steps$,
        synthesizedCheckListConfig: checkListConfig,
        forceCompletionBeforeProceeding,
        sellerOnboardingCompleted: false,
        config: publicConfig.seller_onboarding,
        shouldPresent: true,
      }),
      [
        checkListConfig,
        forceCompletionBeforeProceeding,
        publicConfig.seller_onboarding,
        steps$,
      ],
    );

    useEffect(() => {
      props$.next(props);
    }, [props$, props]);

    return null;
  },
);

const useSynthesizedCheckListConfig = ({
  profile,
  privateProfile,
  readonlyProfile,
  store,
  publicConfig,
}: {
  profile: WithID<Profile> | null | undefined;
  privateProfile: WithID<PrivateProfile> | null | undefined;
  readonlyProfile: WithID<ReadonlyProfile> | null | undefined;
  store: WithID<StoreFront> | null;
  publicConfig: Partial<PublicConfig>;
}) => {
  const isSeller = profile != null && isSellerProfile(profile);
  const isCreator =
    profile != null && isSeller && profile.storeType === StoreType.Creator;

  const { isPslPayEnabled, isPslPayEnforced } = usePslPay(
    profile,
    privateProfile,
    readonlyProfile,
    store,
    publicConfig,
  );

  const defaultCheckListConfig = useDefaultCheckListConfig({
    isCreator,
    isPslPayEnabled,
    isPslPayEnforced,
  });

  const checkListConfig: SynthesizedSellerOnboardingCheckListConfig =
    useMemo(() => {
      const config = merge(
        {},
        defaultCheckListConfig,
        publicConfig.seller_onboarding?.check_list_config?.default,
        isCreator
          ? publicConfig.seller_onboarding?.check_list_config?.creator
          : publicConfig.seller_onboarding?.check_list_config?.business,
      );
      const minInventoryListingCount =
        config.inventory_listings?.min_listing_count ??
        publicConfig?.seller_onboarding?.min_inventory_listing_count ??
        kDefaultMinimumInventoryListingCount;

      const minPublicListingCount =
        config.store_favorites?.min_listing_count ??
        publicConfig?.seller_onboarding?.min_public_listing_count ??
        kDefaultMinimumPublicListingCount;

      if (!config.inventory_listings) {
        config.inventory_listings = {};
      }
      config.inventory_listings.min_listing_count = minInventoryListingCount;

      if (!config.store_favorites) {
        config.store_favorites = {};
      }
      config.store_favorites.min_listing_count = minPublicListingCount;
      return config;
    }, [
      defaultCheckListConfig,
      isCreator,
      publicConfig.seller_onboarding?.check_list_config?.business,
      publicConfig.seller_onboarding?.check_list_config?.creator,
      publicConfig.seller_onboarding?.check_list_config?.default,
      publicConfig.seller_onboarding?.min_inventory_listing_count,
      publicConfig.seller_onboarding?.min_public_listing_count,
    ]);

  return checkListConfig;
};

const useDefaultCheckListConfig = ({
  isCreator,
  isPslPayEnforced,
  isPslPayEnabled,
}: {
  isCreator: boolean;
  isPslPayEnforced: boolean;
  isPslPayEnabled: boolean;
}) =>
  useMemo(
    () =>
      getDefaultCheckListConfig({
        isCreator,
        isPslPayEnabled,
        isPslPayEnforced,
      }),
    [isCreator, isPslPayEnabled, isPslPayEnforced],
  );
